import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { updateAccessLLMModel } from "api";
import { ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { AccessType, IAccessLLMModel } from "models/IAccessLLMModel";
import { IAzureUser } from "models/IAzureUser";
import { IUser } from "models/IUser";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useEditAccessLLMModelStyles } from "./EditAccessLLMModel.styles";
import { mapAzureUsersToModel } from "utils/generalUtils";
import { getPreselectedUsers } from "utils/dialogUtils";
import { IEditAccessLLMModelProps } from "./EditAccessLLMModel.types";

export const EditAccessLLMModel = (
  props: IEditAccessLLMModelProps,
): JSX.Element => {
  const queryClient = useQueryClient();
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const styles = useEditAccessLLMModelStyles();
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [displayName, setDisplayName] = useState<string>(
    props.accessLLMModel?.displayName ?? ConstantValues.EMPTY_STRING,
  );
  const [selectedAccessType, setSelectedAccessType] = useState<AccessType>(
    props.accessLLMModel?.accessType ?? AccessType.PUBLIC,
  );
  const [selectedUsers, setSelectedUsers] = useState<CustomOption[]>([]);

  useEffect(() => {
    if (props.accessLLMModel?.allowedUsers?.length) {
      const mappedUsers = getPreselectedUsers(
        props.accessLLMModel.allowedUsers,
        users,
      );
      setSelectedUsers(mappedUsers);
    }
  }, [props.accessLLMModel?.allowedUsers, users]);

  const readResourcesForPickerFieldFiltered = useCallback(
    async (searchedValue: string): Promise<IAzureUser[]> => {
      const mappedUsers = mapAzureUsersToModel(users);
      return mappedUsers.filter((resource: IAzureUser) =>
        resource.Name.toLowerCase().includes(searchedValue.toLowerCase()),
      );
    },
    [users],
  );

  const handleDisplayNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setDisplayName(e.target.value);
    if (fieldErrors.displayName) {
      setFieldErrors((prev) => ({ ...prev, displayName: "" }));
    }
  };

  const handleAccessTypeChange = (
    _: React.FormEvent,
    data: { value: string },
  ): void => {
    setSelectedAccessType(data.value as AccessType);
    if (fieldErrors.allowedUsers) {
      setFieldErrors((prev) => ({ ...prev, allowedUsers: "" }));
    }
  };

  const handleSelectedUsers = (selectedOptions: CustomOption[]): void => {
    setSelectedUsers(selectedOptions);
    if (fieldErrors.allowedUsers) {
      setFieldErrors((prev) => ({ ...prev, allowedUsers: "" }));
    }
  };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!displayName.trim()) {
      errors.displayName = "Display name is required";
    }

    if (
      selectedAccessType === AccessType.LIMITED &&
      selectedUsers.length === 0
    ) {
      errors.allowedUsers =
        "You must select at least one allowed user for Limited access";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const updatedModel: IAccessLLMModel = {
        ...props.accessLLMModel,
        name: props.accessLLMModel?.name ?? ConstantValues.EMPTY_STRING,
        displayName: displayName.trim(),
        order: props.accessLLMModel?.order ?? 0,
        accessType: selectedAccessType,
        allowedUsers:
          selectedAccessType === AccessType.LIMITED
            ? selectedUsers
                .map((user) => user.id ?? ConstantValues.EMPTY_STRING)
                .filter((id) => id !== ConstantValues.EMPTY_STRING)
            : [],
      };

      await updateAccessLLMModel({
        modelId: props.accessLLMModel?.id,
        ...updatedModel,
      });
      queryClient.invalidateQueries("allAccessLLMModels");

      props.onDismiss(true);
    } catch (error: unknown) {
      const apiError = error as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (
        apiError.response?.status === 400 &&
        apiError.response?.data?.detail === "Model name already exists"
      ) {
        setFieldErrors({
          general: "An AI model with this name already exists",
        });
      } else {
        console.error("Error saving AI model:", error);
        setFieldErrors({
          general: "An error occurred while saving. Please try again",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const hasIncompleteDetails = !displayName.trim();

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface className={footerStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={() => props.onDismiss(false)}
                disabled={isLoading}
              />
            }
          >
            {props.accessLLMModel ? "Update AI Model" : "Add AI Model"}
          </DialogTitle>
          <DialogContent className={contentStyles.formContainer}>
            {isLoading && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner label="Saving..." />
              </div>
            )}
            <div className={styles.modelNameRow}>
              <Text>Model Name:</Text>
              <Text weight="bold">
                {props.accessLLMModel?.name ?? ConstantValues.EMPTY_STRING}
              </Text>
            </div>
            <Field
              label="Display Name"
              required
              className={styles.fieldSpacing}
              validationState={fieldErrors.displayName ? "error" : "none"}
              validationMessage={fieldErrors.displayName}
            >
              <Input
                value={displayName}
                onChange={handleDisplayNameChange}
                onBlur={() => setDisplayName(displayName.trim())}
                disabled={isLoading}
              />
            </Field>
            <Field label="Access Type" className={styles.fieldSpacing}>
              <RadioGroup
                value={selectedAccessType}
                onChange={handleAccessTypeChange}
                layout="horizontal"
                disabled={isLoading}
              >
                <Radio value={AccessType.PUBLIC} label="Public" />
                <Radio value={AccessType.LIMITED} label="Limited" />
                <Radio value={AccessType.DISABLED} label="Disabled" />
              </RadioGroup>
            </Field>

            {selectedAccessType === AccessType.LIMITED && (
              <Field
                label="Allowed Users"
                required
                validationState={fieldErrors.allowedUsers ? "error" : "none"}
                validationMessage={fieldErrors.allowedUsers}
              >
                <PeoplePicker
                  id="users"
                  handleSelectedItems={handleSelectedUsers}
                  disabled={isLoading}
                  required={true}
                  hasOnlyOurUsers={true}
                  readResourcesForPickerFieldFiltered={
                    readResourcesForPickerFieldFiltered
                  }
                  selectedOptions={selectedUsers}
                />
              </Field>
            )}

            {fieldErrors.general && (
              <div role="alert" className={contentStyles.feedbackErrorBox}>
                {fieldErrors.general}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
